import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import workflowService from '../../../services/workflowService';

const TriggerConfiguration = ({ workflows, onWorkflowUpdate }) => {
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [triggerConfig, setTriggerConfig] = useState({
    trigger_type: 'manual',
    trigger_conditions: {}
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleWorkflowSelect = (workflow) => {
    setSelectedWorkflow(workflow);
    setTriggerConfig({
      trigger_type: workflow?.trigger_type || 'manual',
      trigger_conditions: workflow?.trigger_conditions || {}
    });
    setError(null);
  };

  const handleTriggerTypeChange = (triggerType) => {
    setTriggerConfig(prev => ({
      ...prev,
      trigger_type: triggerType,
      trigger_conditions: {} // Reset conditions when type changes
    }));
  };

  const handleConditionChange = (key, value) => {
    setTriggerConfig(prev => ({
      ...prev,
      trigger_conditions: {
        ...prev?.trigger_conditions,
        [key]: value
      }
    }));
  };

  const handleSave = async () => {
    if (!selectedWorkflow?.id) {
      setError('Please select a workflow first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await workflowService?.updateWorkflow(
        selectedWorkflow?.id,
        triggerConfig
      );

      if (updateError) {
        setError(updateError);
        return;
      }

      onWorkflowUpdate?.();
      setSelectedWorkflow(null);
    } catch (err) {
      setError('Failed to update trigger configuration');
    } finally {
      setLoading(false);
    }
  };

  const triggerTypes = [
    {
      value: 'manual',
      label: 'Manual Trigger',
      description: 'Workflow starts when manually triggered',
      icon: 'Hand'
    },
    {
      value: 'time_based',
      label: 'Time-based',
      description: 'Workflow starts at specific times or intervals',
      icon: 'Clock'
    },
    {
      value: 'event_based',
      label: 'Event-based',
      description: 'Workflow starts when specific events occur',
      icon: 'Zap'
    },
    {
      value: 'conditional',
      label: 'Conditional',
      description: 'Workflow starts when conditions are met',
      icon: 'GitBranch'
    }
  ];

  const renderTriggerConditions = () => {
    switch (triggerConfig?.trigger_type) {
      case 'time_based':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Schedule Type
                </label>
                <select
                  value={triggerConfig?.trigger_conditions?.schedule_type || 'once'}
                  onChange={(e) => handleConditionChange('schedule_type', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                >
                  <option value="once">One-time</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Start Time
                </label>
                <Input
                  type="datetime-local"
                  value={triggerConfig?.trigger_conditions?.start_time || ''}
                  onChange={(e) => handleConditionChange('start_time', e?.target?.value)}
                />
              </div>
            </div>
            {triggerConfig?.trigger_conditions?.schedule_type === 'weekly' && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Days of Week
                </label>
                <div className="flex flex-wrap gap-2">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']?.map(day => (
                    <label key={day} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={triggerConfig?.trigger_conditions?.days_of_week?.includes(day) || false}
                        onChange={(e) => {
                          const days = triggerConfig?.trigger_conditions?.days_of_week || [];
                          const updatedDays = e?.target?.checked 
                            ? [...days, day]
                            : days?.filter(d => d !== day);
                          handleConditionChange('days_of_week', updatedDays);
                        }}
                        className="rounded border-border"
                      />
                      <span className="text-sm text-foreground capitalize">{day}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'event_based':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Event Type
              </label>
              <select
                value={triggerConfig?.trigger_conditions?.event_type || 'contact_created'}
                onChange={(e) => handleConditionChange('event_type', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              >
                <option value="contact_created">New Contact Created</option>
                <option value="email_opened">Email Opened</option>
                <option value="email_clicked">Email Link Clicked</option>
                <option value="form_submitted">Form Submitted</option>
                <option value="call_completed">Call Completed</option>
                <option value="lead_score_change">Lead Score Changed</option>
              </select>
            </div>
            {triggerConfig?.trigger_conditions?.event_type === 'lead_score_change' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Minimum Score
                  </label>
                  <Input
                    type="number"
                    value={triggerConfig?.trigger_conditions?.min_score || ''}
                    onChange={(e) => handleConditionChange('min_score', parseInt(e?.target?.value))}
                    placeholder="0"
                    min="0"
                    max="100"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Maximum Score
                  </label>
                  <Input
                    type="number"
                    value={triggerConfig?.trigger_conditions?.max_score || ''}
                    onChange={(e) => handleConditionChange('max_score', parseInt(e?.target?.value))}
                    placeholder="100"
                    min="0"
                    max="100"
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 'conditional':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Condition Field
              </label>
              <select
                value={triggerConfig?.trigger_conditions?.field || 'lead_score'}
                onChange={(e) => handleConditionChange('field', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              >
                <option value="lead_score">Lead Score</option>
                <option value="contact_status">Contact Status</option>
                <option value="last_contact_date">Last Contact Date</option>
                <option value="company_size">Company Size</option>
                <option value="industry">Industry</option>
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Operator
                </label>
                <select
                  value={triggerConfig?.trigger_conditions?.operator || 'equals'}
                  onChange={(e) => handleConditionChange('operator', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                >
                  <option value="equals">Equals</option>
                  <option value="not_equals">Not Equals</option>
                  <option value="greater_than">Greater Than</option>
                  <option value="less_than">Less Than</option>
                  <option value="contains">Contains</option>
                  <option value="not_contains">Not Contains</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Value
                </label>
                <Input
                  type="text"
                  value={triggerConfig?.trigger_conditions?.value || ''}
                  onChange={(e) => handleConditionChange('value', e?.target?.value)}
                  placeholder="Enter condition value"
                />
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="Hand" size={48} className="mx-auto mb-4 opacity-50" />
            <p>Manual triggers require no additional configuration.</p>
            <p className="text-sm mt-2">This workflow will start when manually triggered.</p>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Workflow Selection */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Select Workflow to Configure
        </h3>
        
        {workflows?.length === 0 ? (
          <p className="text-muted-foreground">No workflows available. Create a workflow first.</p>
        ) : (
          <div className="grid gap-3">
            {workflows?.map(workflow => (
              <div
                key={workflow?.id}
                onClick={() => handleWorkflowSelect(workflow)}
                className={`
                  p-4 border rounded-lg cursor-pointer transition-colors
                  ${selectedWorkflow?.id === workflow?.id
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
                  }
                `}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-foreground">{workflow?.name}</h4>
                    <p className="text-sm text-muted-foreground">
                      Current trigger: {workflow?.trigger_type?.replace('_', ' ')}
                    </p>
                  </div>
                  <div className={`
                    px-2 py-1 rounded text-xs font-medium
                    ${workflow?.status === 'active' ? 'bg-success/10 text-success' :
                      workflow?.status === 'draft' ? 'bg-muted text-muted-foreground' :
                      'bg-warning/10 text-warning'}
                  `}>
                    {workflow?.status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Trigger Configuration */}
      {selectedWorkflow && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-foreground">
              Configure Trigger for "{selectedWorkflow?.name}"
            </h3>
            <Button
              onClick={() => setSelectedWorkflow(null)}
              variant="ghost"
              iconName="X"
            />
          </div>

          {error && (
            <div className="mb-6 bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-destructive" />
                <span className="text-destructive text-sm">{error}</span>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* Trigger Type Selection */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-4">
                Trigger Type
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {triggerTypes?.map(type => (
                  <div
                    key={type?.value}
                    onClick={() => handleTriggerTypeChange(type?.value)}
                    className={`
                      p-4 border rounded-lg cursor-pointer transition-colors
                      ${triggerConfig?.trigger_type === type?.value
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                      }
                    `}
                  >
                    <div className="flex items-start space-x-3">
                      <Icon name={type?.icon} size={20} className="text-primary mt-0.5" />
                      <div>
                        <h4 className="font-medium text-foreground">{type?.label}</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          {type?.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Trigger Conditions */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-4">
                Trigger Conditions
              </label>
              <div className="border border-border rounded-lg p-4">
                {renderTriggerConditions()}
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleSave}
                disabled={loading}
                iconName={loading ? "Loader2" : "Save"}
                iconPosition="left"
                className={loading ? "animate-spin" : ""}
              >
                {loading ? 'Saving...' : 'Save Trigger Configuration'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TriggerConfiguration;